import { useState, useCallback, useMemo } from 'react';
import { Check, Copy, Terminal } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CodeBlockProps {
  children: string;
  /** Show terminal-style header with traffic lights */
  showHeader?: boolean;
  /** Label shown in header (e.g., "Terminal", "bash", "zsh") */
  label?: string;
  /** Show line numbers */
  showLineNumbers?: boolean;
  /** Show terminal prompt ($) before commands */
  showPrompt?: boolean;
  /** Highlight CLI syntax (flags, strings, etc.) */
  highlightSyntax?: boolean;
  /** Additional class names */
  className?: string;
  /** Compact mode for inline-ish display */
  compact?: boolean;
}

interface InlineCodeProps {
  children: React.ReactNode;
  className?: string;
}

// Syntax highlighting tokens for CLI commands
type TokenType = 'command' | 'flag' | 'string' | 'variable' | 'operator' | 'comment' | 'path' | 'number' | 'plain';

interface Token {
  type: TokenType;
  value: string;
}

const tokenColors: Record<TokenType, string> = {
  command: 'text-primary font-medium',
  flag: 'text-amber-400',
  string: 'text-emerald-400',
  variable: 'text-purple-400',
  operator: 'text-muted-foreground',
  comment: 'text-muted-foreground/60 italic',
  path: 'text-cyan-400',
  number: 'text-orange-400',
  plain: 'text-foreground',
};

function tokenizeLine(line: string): Token[] {
  const tokens: Token[] = [];
  let remaining = line;
  let isFirstWord = true;

  while (remaining.length > 0) {
    // Skip leading whitespace
    const wsMatch = remaining.match(/^(\s+)/);
    if (wsMatch) {
      tokens.push({ type: 'plain', value: wsMatch[1] });
      remaining = remaining.slice(wsMatch[1].length);
      continue;
    }

    // Comments (# ...)
    if (remaining.startsWith('#')) {
      tokens.push({ type: 'comment', value: remaining });
      break;
    }

    // Double-quoted strings
    const dqMatch = remaining.match(/^"([^"\\]|\\.)*"/);
    if (dqMatch) {
      tokens.push({ type: 'string', value: dqMatch[0] });
      remaining = remaining.slice(dqMatch[0].length);
      isFirstWord = false;
      continue;
    }

    // Single-quoted strings
    const sqMatch = remaining.match(/^'([^'\\]|\\.)*'/);
    if (sqMatch) {
      tokens.push({ type: 'string', value: sqMatch[0] });
      remaining = remaining.slice(sqMatch[0].length);
      isFirstWord = false;
      continue;
    }

    // Environment variables ($VAR or ${VAR})
    const varMatch = remaining.match(/^\$\{?[A-Za-z_][A-Za-z0-9_]*\}?/);
    if (varMatch) {
      tokens.push({ type: 'variable', value: varMatch[0] });
      remaining = remaining.slice(varMatch[0].length);
      isFirstWord = false;
      continue;
    }

    // Operators (|, >, >>, <, &&, ||, ;)
    const opMatch = remaining.match(/^(&&|\|\||>>|[|><;])/);
    if (opMatch) {
      tokens.push({ type: 'operator', value: opMatch[0] });
      remaining = remaining.slice(opMatch[0].length);
      isFirstWord = true; // Next word after operator is a command
      continue;
    }

    // Flags (--flag or -f)
    const flagMatch = remaining.match(/^--?[a-zA-Z][a-zA-Z0-9-]*/);
    if (flagMatch) {
      tokens.push({ type: 'flag', value: flagMatch[0] });
      remaining = remaining.slice(flagMatch[0].length);
      isFirstWord = false;
      continue;
    }

    // Paths (starts with ./ or / or ~/)
    const pathMatch = remaining.match(/^[.~]?\/[^\s]*/);
    if (pathMatch) {
      tokens.push({ type: 'path', value: pathMatch[0] });
      remaining = remaining.slice(pathMatch[0].length);
      isFirstWord = false;
      continue;
    }

    // Numbers
    const numMatch = remaining.match(/^\d+(\.\d+)?/);
    if (numMatch && !isFirstWord) {
      tokens.push({ type: 'number', value: numMatch[0] });
      remaining = remaining.slice(numMatch[0].length);
      continue;
    }

    // Words (command or argument)
    const wordMatch = remaining.match(/^[^\s]+/);
    if (wordMatch) {
      const word = wordMatch[0];
      // Check if it's a known command or first word in the line
      const knownCommands = [
        'keyenv', 'npm', 'npx', 'pnpm', 'yarn', 'node', 'deno', 'bun',
        'git', 'docker', 'kubectl', 'aws', 'gcloud', 'az', 'terraform',
        'curl', 'wget', 'ssh', 'scp', 'rsync', 'make', 'cargo', 'go',
        'python', 'pip', 'python3', 'pip3', 'ruby', 'gem', 'bundle',
        'brew', 'apt', 'apt-get', 'yum', 'dnf', 'pacman',
        'cd', 'ls', 'cat', 'echo', 'export', 'source', 'eval', 'exec',
        'mkdir', 'rm', 'cp', 'mv', 'touch', 'chmod', 'chown',
        'grep', 'sed', 'awk', 'find', 'xargs', 'head', 'tail',
        'sudo', 'su', 'env', 'which', 'whereis', 'type',
      ];

      if (isFirstWord || knownCommands.includes(word.toLowerCase())) {
        tokens.push({ type: 'command', value: word });
      } else {
        tokens.push({ type: 'plain', value: word });
      }
      remaining = remaining.slice(word.length);
      isFirstWord = false;
      continue;
    }

    // Fallback: take one character
    tokens.push({ type: 'plain', value: remaining[0] });
    remaining = remaining.slice(1);
  }

  return tokens;
}

function HighlightedLine({ line }: { line: string }) {
  const tokens = useMemo(() => tokenizeLine(line), [line]);

  return (
    <>
      {tokens.map((token, i) => (
        <span key={i} className={tokenColors[token.type]}>
          {token.value}
        </span>
      ))}
    </>
  );
}

export function CodeBlock({
  children,
  showHeader = false,
  label = 'Terminal',
  showLineNumbers = false,
  showPrompt = true,
  highlightSyntax = true,
  className,
  compact = false,
}: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [children]);

  const lines = useMemo(() => children.trim().split('\n'), [children]);

  return (
    <div
      className={cn(
        'group relative rounded-lg overflow-hidden',
        'bg-[hsl(225_20%_4%)] border border-border/60',
        'shadow-lg shadow-black/20',
        className
      )}
    >
      {/* Terminal header */}
      {showHeader && (
        <div className="flex items-center justify-between px-4 py-2.5 bg-[hsl(225_15%_8%)] border-b border-border/40">
          <div className="flex items-center gap-2">
            {/* Traffic lights */}
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-[#ff5f57] shadow-[inset_0_-1px_1px_rgba(0,0,0,0.2)]" />
              <div className="w-3 h-3 rounded-full bg-[#febc2e] shadow-[inset_0_-1px_1px_rgba(0,0,0,0.2)]" />
              <div className="w-3 h-3 rounded-full bg-[#28c840] shadow-[inset_0_-1px_1px_rgba(0,0,0,0.2)]" />
            </div>
            {/* Label */}
            <div className="flex items-center gap-1.5 ml-3 text-xs text-muted-foreground">
              <Terminal className="w-3.5 h-3.5" />
              <span>{label}</span>
            </div>
          </div>
          {/* Copy button in header */}
          <button
            onClick={handleCopy}
            className={cn(
              'flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-all duration-200',
              copied
                ? 'bg-success/20 text-success'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            )}
          >
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5" />
                <span>Copied!</span>
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" />
                <span>Copy</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Code content */}
      <div className={cn('relative', compact ? 'p-2 pr-10' : 'p-4')}>
        {/* Floating copy button when no header */}
        {!showHeader && (
          <button
            onClick={handleCopy}
            className={cn(
              'absolute z-10',
              'flex items-center justify-center rounded-md',
              'transition-all duration-200',
              compact
                ? 'top-1/2 -translate-y-1/2 right-1.5 w-7 h-7'
                : 'top-2 right-2 w-8 h-8',
              copied
                ? 'bg-success/20 text-success'
                : 'bg-muted/80 text-muted-foreground hover:text-foreground hover:bg-muted'
            )}
            title={copied ? 'Copied!' : 'Copy to clipboard'}
          >
            {copied ? <Check className={cn(compact ? 'w-3.5 h-3.5' : 'w-4 h-4')} /> : <Copy className={cn(compact ? 'w-3.5 h-3.5' : 'w-4 h-4')} />}
          </button>
        )}

        {/* Scanline effect for terminal feel */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.015]"
          style={{
            backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.03) 2px, rgba(255,255,255,0.03) 4px)',
          }}
        />

        <pre className={cn('font-mono text-sm leading-relaxed overflow-x-auto', compact && 'text-xs')}>
          <code>
            {lines.map((line, i) => (
              <div key={i} className="flex">
                {/* Line numbers */}
                {showLineNumbers && (
                  <span className="select-none pr-4 text-muted-foreground/40 text-right min-w-[2.5rem]">
                    {i + 1}
                  </span>
                )}
                {/* Prompt */}
                {showPrompt && (
                  <span className="select-none text-emerald-500 mr-2 font-semibold">$</span>
                )}
                {/* Code line */}
                <span className="flex-1">
                  {highlightSyntax ? <HighlightedLine line={line} /> : line}
                </span>
              </div>
            ))}
          </code>
        </pre>
      </div>

      {/* Subtle glow effect on hover */}
      <div className="absolute inset-0 rounded-lg pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300 ring-1 ring-inset ring-primary/10" />
    </div>
  );
}

/**
 * Inline code component for single commands or code snippets within text
 */
export function InlineCode({ children, className }: InlineCodeProps) {
  return (
    <code
      className={cn(
        'px-1.5 py-0.5 rounded-md font-mono text-[0.8125rem]',
        'bg-[hsl(225_20%_10%)] text-primary/90',
        'border border-border/50',
        className
      )}
    >
      {children}
    </code>
  );
}

/**
 * Command display - specialized for single CLI commands with copy
 */
interface CommandProps {
  children: string;
  className?: string;
}

export function Command({ children, className }: CommandProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [children]);

  const tokens = useMemo(() => tokenizeLine(children.trim()), [children]);

  return (
    <div
      className={cn(
        'group flex items-center gap-2 px-3 py-1.5 rounded-lg',
        'bg-[hsl(225_20%_6%)] border border-border/60',
        'hover:border-primary/30 transition-colors duration-200',
        className
      )}
    >
      <span className="text-emerald-500 font-semibold font-mono text-sm select-none">$</span>
      <code className="font-mono text-sm flex-1 overflow-x-auto">
        {tokens.map((token, i) => (
          <span key={i} className={tokenColors[token.type]}>
            {token.value}
          </span>
        ))}
      </code>
      <button
        onClick={handleCopy}
        className={cn(
          'ml-1 p-1.5 rounded-md transition-all duration-200 flex-shrink-0',
          copied
            ? 'bg-success/20 text-success'
            : 'bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-muted'
        )}
        title={copied ? 'Copied!' : 'Copy'}
      >
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

export default CodeBlock;
